"use client";

import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import type {
  CShopCartConfig,
  CShopCartContent,
  CShopCartProduct,
  CShopCartTotal,
  CShopDeliveryOption,
} from "@/lib/api/cms-catalog";
import { useT } from "@/lib/i18n/context";
import { toast } from "@/lib/notifications/toast";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import { cShopTranslations } from "./CShop.i18n";
import styles from "./CShop.module.css";
import {
  CartLine,
  CartSummary,
  DeliveryOptions,
  DiscountCode,
  FreeDeliveryBanner,
  OrderSteps,
  PaymentOptions,
} from "./CShop";
import { CShopInpostPicker } from "./CShopInpostPicker";
import { CShopPickupPointPicker } from "./CShopPickupPointPicker";

/* ─── Cart view (pelna strona koszyka - krok 1 flow zamawiania) ───────── */

/**
 * Optimistic overlay dla podsumowania. Trzymamy tu ostatni znany `total` +
 * `config` po sukcesie mutacji zeby UI nie migotalo miedzy odpowiedzia
 * z `/api/c-shop/update-card` a `router.refresh()` (ktory zaciaga swiezy
 * `content` z pageBootstrap). Wartosci `null` = uzyj tego co przyszlo w
 * `content`.
 */
type CartOverlay = {
  total: CShopCartTotal | null;
  config: CShopCartConfig | null;
  deliveryOptions: CShopDeliveryOption[] | null;
};

export function CShopCart({ content }: { content: CShopCartContent }) {
  const t = useT(cShopTranslations);
  const router = useRouter();

  const [products, setProducts] = useState<CShopCartProduct[]>(content.products);
  const [busyIds, setBusyIds] = useState<ReadonlySet<number>>(new Set());
  const [overlay, setOverlay] = useState<CartOverlay>({
    total: null,
    config: null,
    deliveryOptions: null,
  });

  useEffect(() => {
    /* Po router.refresh() przychodzi swiezy `content` z pageBootstrap - overlay
       przestaje byc potrzebny i wracamy do source-of-truth. */
    setProducts(content.products);
    setOverlay({ total: null, config: null, deliveryOptions: null });
  }, [content.products, content.total, content.config]);

  const total = overlay.total ?? content.total;
  const config = overlay.config ?? content.config;
  const deliveryOptions = overlay.deliveryOptions ?? content.delivery_options;

  const setItemBusy = useCallback((cId: number, busy: boolean) => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(cId);
      else next.delete(cId);
      return next;
    });
  }, []);

  const updateCount = useCallback(
    async (cId: number, nextCount: number) => {
      if (content.card_id === null) return;
      const previous = products;
      const optimistic = previous.map((p) =>
        p.c_id === cId ? { ...p, count: nextCount } : p,
      );
      setProducts(optimistic);
      setItemBusy(cId, true);
      try {
        const res = await fetch("/api/w-card/update-count", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            card_id: content.card_id,
            product_index: cId,
            count: nextCount,
          }),
        });
        if (!res.ok) throw new Error(t.cart.updateFailed);
        /* Router refresh sciaga swiezy snapshot koszyka z backendu - nowe sumy
           uwzgledniaja rabaty/promocje ktore mogly sie zmienic. */
        router.refresh();
      } catch {
        setProducts(previous);
        toast.error(t.cart.updateFailed);
      } finally {
        setItemBusy(cId, false);
      }
    },
    [content.card_id, products, router, setItemBusy, t.cart.updateFailed],
  );

  const deleteItem = useCallback(
    async (cId: number) => {
      if (content.card_id === null) return;
      const previous = products;
      const optimistic = previous.filter((p) => p.c_id !== cId);
      setProducts(optimistic);
      setItemBusy(cId, true);
      try {
        const res = await fetch("/api/w-card/delete-product", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            card_id: content.card_id,
            product_index: cId,
          }),
        });
        if (!res.ok) throw new Error(t.cart.deleteFailed);
        router.refresh();
      } catch {
        setProducts(previous);
        toast.error(t.cart.deleteFailed);
      } finally {
        setItemBusy(cId, false);
      }
    },
    [content.card_id, products, router, setItemBusy, t.cart.deleteFailed],
  );

  const listHref = extractInternalHref(content.href.list);
  const orderHref = extractInternalHref(content.href.order);
  const currency = content.currency ?? "";

  /* Walidacja kroku 1 (parity z legacy isStepValid(1)) - blokada przycisku
     "Przejdz do zamawiania" gdy: (a) brak wybranej platnosci, (b) brak wybranej
     dostawy, (c) ktorakolwiek pozycja ma count > stock, (d) dostawa integration=inpost
     bez wybranego paczkomatu, (e) wybrana platnosc online, ale total przekroczyl
     `shop_online_payment_max_amount` (opcja przyszla z disabled=true - user musi
     przelaczyc). Hint pod przyciskami tlumaczy user'owi ktore pole poprawic. */
  const hasPayment = config.payment_option.id !== null;
  const hasDelivery = config.delivery_option.id !== null;
  const hasStockIssue = products.some((p) => p.count > p.stock);
  const isInpostDelivery = config.delivery_option.integration === "inpost";
  const inpostParcelMissing = isInpostDelivery && config.inpost_parcel === null;
  /* Type z DB moze przyjsc jako int (natywnie z INT column) lub string (po
     serialize card.config); Number() unifikuje. 8 = M_SHOP_DELIVERY_PICKUP_POINT. */
  const isPickupDelivery = Number(config.delivery_option.type) === 8;
  const pickupPointMissing = isPickupDelivery && config.pickup_point === null;
  const selectedPaymentDisabled = content.payment_options.some(
    (o) => o.id === config.payment_option.id && o.disabled,
  );
  const gotoOrderDisabled =
    !hasPayment ||
    !hasDelivery ||
    hasStockIssue ||
    inpostParcelMissing ||
    pickupPointMissing ||
    selectedPaymentDisabled;
  const gotoOrderHint = hasStockIssue
    ? t.cart.gotoOrderDisabledStock
    : !hasPayment
      ? t.cart.gotoOrderDisabledNoPayment
      : !hasDelivery
        ? t.cart.gotoOrderDisabledNoDelivery
        : inpostParcelMissing
          ? t.inpost.gotoOrderRequired
          : pickupPointMissing
            ? t.pickupPoint.gotoOrderRequired
            : selectedPaymentDisabled
              ? t.payment.gotoOrderDisabledOverMax
              : null;


  if (products.length === 0) {
    return (
      <div className={styles.root}>
        <OrderSteps steps={content.steps} />
        <div className={styles.empty}>{t.cart.empty}</div>
        {listHref !== null && (
          <div className={styles.actions}>
            <div className={styles.actionsLeft}>
              <Link href={listHref} className={styles.buttonSecondary}>
                {t.cart.continueShopping}
              </Link>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <OrderSteps steps={content.steps} />

      <FreeDeliveryBanner
        threshold={content.free_delivery_over_price}
        isFree={config.is_free_delivery}
        remaining={config.to_free_delivery}
        currency={currency}
        t={t}
      />

      <ul className={styles.list}>
        {products.map((product) => (
          <CartLine
            key={product.c_id}
            product={product}
            currency={currency}
            busy={busyIds.has(product.c_id)}
            onChangeCount={updateCount}
            onDelete={deleteItem}
            t={t}
          />
        ))}
      </ul>

      <div className={styles.bottomGrid}>
        <div className={styles.optionsColumn}>
          <PaymentOptions
            cardId={content.card_id}
            options={content.payment_options}
            selectedId={config.payment_option.id}
            currency={currency}
            onChanged={(cfg, tot, deliv) =>
              setOverlay({
                total: tot,
                config: cfg,
                deliveryOptions: deliv ?? [],
              })
            }
            t={t}
          />
          <DeliveryOptions
            cardId={content.card_id}
            options={deliveryOptions}
            selectedId={config.delivery_option.id}
            hasPayment={config.payment_option.id !== null}
            currency={currency}
            onChanged={(cfg, tot) =>
              setOverlay((prev) => ({
                total: tot,
                config: cfg,
                deliveryOptions: prev.deliveryOptions,
              }))
            }
            t={t}
          />
          {isInpostDelivery && (
            <CShopInpostPicker
              cardId={content.card_id}
              selection={config.inpost_parcel}
              googleApiKey={content.google_api_key}
              hasValidationError={inpostParcelMissing}
              t={t}
              onChanged={(cfg, tot, deliv) =>
                setOverlay({
                  total: tot,
                  config: cfg,
                  deliveryOptions: deliv ?? deliveryOptions,
                })
              }
            />
          )}
          {content.discount_codes_available && (
            <DiscountCode
              cardId={content.card_id}
              currentCode={config.discount_code.code}
              hasApplied={config.discount_code.id !== null}
              onChanged={(cfg, tot) =>
                setOverlay((prev) => ({
                  total: tot,
                  config: cfg,
                  deliveryOptions: prev.deliveryOptions,
                }))
              }
              t={t}
            />
          )}
        </div>
        <CartSummary total={total} currency={currency} t={t} />
      </div>

      <div className={styles.actions}>
        <div className={styles.actionsLeft}>
          {listHref !== null && (
            <Link href={listHref} className={styles.buttonSecondary}>
              {t.cart.continueShopping}
            </Link>
          )}
        </div>
        <div className={styles.actionsRight}>
          {/* Walidacja kroku 1: przycisk zablokowany (aria-disabled + native
              `<button disabled>`) gdy brak platnosci/dostawy lub stan
              magazynowy niewystarczajacy. Powod na hover jako `title` +
              zdublowany w `aria-label` (screen reader nie czyta title). */}
          {gotoOrderDisabled ? (
            <button
              type="button"
              className={styles.buttonPrimary}
              disabled
              aria-disabled="true"
              title={gotoOrderHint ?? undefined}
              aria-label={
                gotoOrderHint !== null
                  ? `${t.cart.gotoOrder} - ${gotoOrderHint}`
                  : undefined
              }
            >
              {t.cart.gotoOrder}
            </button>
          ) : orderHref !== null ? (
            <Link href={orderHref} className={styles.buttonPrimary}>
              {t.cart.gotoOrder}
            </Link>
          ) : (
            <a href={content.href.order} className={styles.buttonPrimary}>
              {t.cart.gotoOrder}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
